/**
 * trace-format.ts — Định dạng thuần của hộp "số này ở đâu ra" (trace-dialog.tsx): tổng, từng ô dữ liệu, và dạng THẺ cho điện thoại
 * (mỗi dòng dữ liệu → một thẻ: danh sách nhãn / giá trị, thay cho bảng cuộn ngang).
 */
import dayjs from 'dayjs'
import type { ChiSoTruyVet, CotTruyVet } from '@/hooks/use-drilldown'
import { formatNumber } from '@/lib/utils'

/** Chỉ số nào hiển thị dạng tiền / phần trăm thay vì số đếm. */
const TIEN: ChiSoTruyVet[] = ['revenue']
const PHAN_TRAM: ChiSoTruyVet[] = ['msgPerFriend', 'afterHoursPct', 'convRate', 'aiPct']

export function dinhDangTong(metric: ChiSoTruyVet, v: number): string {
  if (TIEN.includes(metric)) return `${formatNumber(Math.round(v))} ₫`
  if (PHAN_TRAM.includes(metric)) {
    // Không kẹp trần 100%: tin nhắn / liên hệ vượt 100% là chuyện bình thường
    // khi một liên hệ nhắn nhiều lượt trong kỳ.
    return `${(v * 100).toLocaleString('vi-VN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
  }
  return formatNumber(v)
}

/** Một ô dữ liệu theo kiểu cột. Trống → "—". */
export function oO(v: unknown, cot: CotTruyVet): string {
  if (v == null || v === '') return '—'
  switch (cot.kieu) {
    case 'ngay':
      return dayjs(v as string).format('DD/MM/YYYY HH:mm')
    case 'tien':
      return `${formatNumber(Math.round(Number(v)))} ₫`
    case 'so':
      return formatNumber(Number(v))
    default:
      return String(v)
  }
}

/** Cột nội dung dài (tin nhắn…) — trên thẻ chiếm cả hàng. */
export const COT_NOI_DUNG = 'noi_dung'
/** Giá trị dài hơn bấy nhiêu ký tự thì chiếm cả hàng trên thẻ (nửa hàng ≈ 22 ký tự ở màn 375px). */
export const RONG_TU = 22

export interface Truong {
  key: string
  nhan: string
  value: string
  /** Chiếm cả hàng (nội dung dài) thay vì nửa hàng. */
  rong: boolean
  /** Số / tiền — căn số đều hàng. */
  so: boolean
}

/**
 * Dòng dữ liệu → các trường của thẻ điện thoại. Giữ nguyên thứ tự cột; ô trống vẫn hiện "—" để thẻ nào cũng cùng một khuôn.
 * Cột đầu tiên (thường là thời gian / tên) do giao diện làm tiêu đề thẻ, nên có thể tách bằng `tachTieuDe`.
 */
export function truongCuaThe(cot: CotTruyVet[], dong: Record<string, unknown>): Truong[] {
  return cot.map((c) => {
    const value = oO(dong[c.key], c)
    return { key: c.key, nhan: c.nhan, value, rong: c.key === COT_NOI_DUNG || value.length > RONG_TU, so: c.kieu === 'so' || c.kieu === 'tien' }
  })
}

/** Tách trường đầu làm tiêu đề thẻ; còn lại là phần thân. Không có cột → không có tiêu đề. */
export function tachTieuDe(truong: Truong[]): { tieuDe?: Truong; than: Truong[] } {
  return truong.length === 0 ? { than: [] } : { tieuDe: truong[0], than: truong.slice(1) }
}
